"use client";

/**
 * useHomeShowcaseData — Home page showcase data hook
 *
 * Fetches and caches data for:
 * - Continue Watch (auth-gated)
 * - You May Like (auth-gated)
 *
 * Uses stale-while-revalidate pattern:
 * 1. Show cached data immediately
 * 2. Fetch fresh data in background
 * 3. Update UI when fresh data arrives
 *
 * Ported from App-WebTV/src/utils/hooks/useHomeShowcaseData.js
 * Adapted for FrontendClient (Next.js) using axiosInstance and Cookies.
 */
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import Cookies from "js-cookie";
import {
    setContinueWatch as reduxSetContinueWatch,
    setYouMayLike as reduxSetYouMayLike,
} from "@/redux/slices/showcase.slice";

// ============================================================================
// CACHE CONFIGURATION
// ============================================================================

const CACHE_VERSION = "1.0";
const CACHE_PREFIX = "home_showcase_";

const getCachedData = (key) => {
    if (typeof window === "undefined") return null;
    try {
        const cached = localStorage.getItem(CACHE_PREFIX + key);
        if (!cached) return null;

        const parsed = JSON.parse(cached);
        if (parsed.version !== CACHE_VERSION) {
            localStorage.removeItem(CACHE_PREFIX + key);
            return null;
        }

        return parsed.data;
    } catch (error) {
        console.warn(
            `[useHomeShowcaseData] Error reading cache for ${key}:`,
            error
        );
        return null;
    }
};

const setCachedData = (key, data) => {
    if (typeof window === "undefined") return;
    try {
        const cacheEntry = {
            data,
            timestamp: Date.now(),
            version: CACHE_VERSION,
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    } catch (error) {
        console.warn(
            `[useHomeShowcaseData] Error writing cache for ${key}:`,
            error
        );
    }
};

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

/**
 * Check if user is logged in (client-side check via cookies)
 */
const isUserLoggedIn = () => {
    try {
        const token = Cookies.get("userAccessToken");
        return !!token && token.length > 0;
    } catch {
        return false;
    }
};

/**
 * Transform continue-watch items to flatten nested content structure
 * API returns: { content: {...}, elapsedTime, totalTime, updatedAt, watchedEpisode? }
 * Cards expect: { ...contentData, elapsedTime, totalTime, seasonNumber, episodeNumber }
 */
const transformContinueWatch = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        const content = item.content || {};
        const options = item.options || {};
        const watchedEpisode =
            content.watchedEpisode || item.watchedEpisode || {};

        // Extract season/episode from all possible locations
        const rawSeason =
            item.seasonNumber ??
            item.season ??
            content.seasonNumber ??
            content.season ??
            options.seasonNumber ??
            options.season ??
            watchedEpisode.seasonNumber ??
            watchedEpisode.season;

        const rawEpisode =
            item.episodeNumber ??
            item.episode ??
            content.episodeNumber ??
            content.episode ??
            options.episodeNumber ??
            options.episode ??
            watchedEpisode.episodeNumber ??
            watchedEpisode.episode;

        const seasonNumber =
            typeof rawSeason === "number"
                ? rawSeason
                : typeof rawSeason === "string"
                    ? parseInt(rawSeason, 10)
                    : null;
        const episodeNumber =
            typeof rawEpisode === "number"
                ? rawEpisode
                : typeof rawEpisode === "string"
                    ? parseInt(rawEpisode, 10)
                    : null;

        return {
            ...content,
            elapsedTime: item.elapsedTime || 0,
            totalTime: item.totalTime || 0,
            updatedAt: item.updatedAt,
            seasonNumber: isNaN(seasonNumber) ? null : seasonNumber,
            episodeNumber: isNaN(episodeNumber) ? null : episodeNumber,
        };
    });
};

/**
 * Extract results array from API response
 * Handles { data: { results: [...] } } structure
 */
const extractResults = (response, isContinueWatch = false) => {
    try {
        let results = [];

        if (response?.data?.data?.results) {
            results = response.data.data.results;
        } else if (response?.data?.results) {
            results = response.data.results;
        }

        if (isContinueWatch && results.length > 0) {
            results = transformContinueWatch(results);
        }

        return results;
    } catch {
        return [];
    }
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Custom hook for fetching home showcase data
 *
 * @returns {Object} { continueWatch, youMayLike, loading, isLoggedIn }
 */
export function useHomeShowcaseData() {
    const dispatch = useDispatch();
    // Read from Redux for reactive updates when player dispatches fetchContinueWatch
    const reduxContinueWatch = useSelector((state) => state.Showcase?.continueWatch);
    const [continueWatch, setContinueWatch] = useState(null);
    const [youMayLike, setYouMayLike] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const initRef = useRef(false);

    // Fetch all data
    const fetchAllData = useCallback(async () => {
        const loggedIn = isUserLoggedIn();
        setIsLoggedIn(loggedIn);

        const promises = [];
        const promiseKeys = [];

        // Private data (only if logged in)
        if (loggedIn) {
            promises.push(
                axiosInstance
                    .get(getCMSApiRoute("showcase/continue-watch"))
                    .catch((err) => {
                        console.error(
                            "[useHomeShowcaseData] continue-watch error:",
                            err
                        );
                        return null;
                    })
            );
            promiseKeys.push("continueWatch");

            promises.push(
                axiosInstance
                    .get(getCMSApiRoute("showcase/user-may-like"))
                    .catch((err) => {
                        console.error(
                            "[useHomeShowcaseData] user-may-like error:",
                            err
                        );
                        return null;
                    })
            );
            promiseKeys.push("youMayLike");
        }

        try {
            const results = await Promise.all(promises);

            const data = {};
            promiseKeys.forEach((key, index) => {
                const isContinueWatch = key === "continueWatch";
                data[key] = extractResults(results[index], isContinueWatch);
            });

            return data;
        } catch (error) {
            console.error("[useHomeShowcaseData] Fetch error:", error);
            return {};
        }
    }, []);

    // Main initialization effect
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        const initialize = async () => {
            // Check for cached data first
            const cachedContinueWatch = getCachedData("continueWatch");
            const cachedYouMayLike = getCachedData("youMayLike");

            const hasCachedData = cachedContinueWatch || cachedYouMayLike;

            if (hasCachedData) {
                // Cache HIT: Show cached data immediately
                if (cachedContinueWatch) setContinueWatch(cachedContinueWatch);
                if (cachedYouMayLike) setYouMayLike(cachedYouMayLike);
                setLoading(false);

                // Background revalidation
                const freshData = await fetchAllData();

                if (freshData.continueWatch?.length > 0) {
                    setContinueWatch(freshData.continueWatch);
                    setCachedData("continueWatch", freshData.continueWatch);
                    dispatch(reduxSetContinueWatch(freshData.continueWatch));
                }
                if (freshData.youMayLike?.length > 0) {
                    setYouMayLike(freshData.youMayLike);
                    setCachedData("youMayLike", freshData.youMayLike);
                    dispatch(reduxSetYouMayLike(freshData.youMayLike));
                }
            } else {
                // Cache MISS: Fetch fresh data
                setLoading(true);

                const freshData = await fetchAllData();

                if (freshData.continueWatch?.length > 0) {
                    setContinueWatch(freshData.continueWatch);
                    setCachedData("continueWatch", freshData.continueWatch);
                    dispatch(reduxSetContinueWatch(freshData.continueWatch));
                }
                if (freshData.youMayLike?.length > 0) {
                    setYouMayLike(freshData.youMayLike);
                    setCachedData("youMayLike", freshData.youMayLike);
                    dispatch(reduxSetYouMayLike(freshData.youMayLike));
                }

                setLoading(false);
            }
        };

        initialize();
    }, [fetchAllData]);

    return {
        // Use Redux continue-watch if available (updated reactively from player exit)
        // Fall back to local state for initial load
        continueWatch: (reduxContinueWatch?.length > 0 ? reduxContinueWatch : continueWatch),
        youMayLike,
        loading,
        isLoggedIn,
    };
}

/**
 * Clear all home showcase cache (call on logout)
 */
export function clearHomeShowcaseCache() {
    if (typeof window === "undefined") return;
    try {
        localStorage.removeItem(CACHE_PREFIX + "continueWatch");
        localStorage.removeItem(CACHE_PREFIX + "youMayLike");
    } catch (error) {
        console.warn("[useHomeShowcaseData] Error clearing cache:", error);
    }
}

/**
 * Refetch continue-watch data and update cache
 * Call this when player exits to refresh the continue-watch row
 * @returns {Promise<Array>} Fresh continue-watch items
 */
export async function refetchContinueWatch() {
    try {
        const token = Cookies.get("userAccessToken");
        if (!token || token.length === 0) {
            return [];
        }

        const response = await axiosInstance.get(
            getCMSApiRoute("showcase/continue-watch")
        );

        let results = [];
        if (response?.data?.data?.results) {
            results = response.data.data.results;
        } else if (response?.data?.results) {
            results = response.data.results;
        }

        if (results.length > 0) {
            results = transformContinueWatch(results);
        }

        setCachedData("continueWatch", results);
        return results;
    } catch (error) {
        console.warn("[refetchContinueWatch] Error:", error);
        return [];
    }
}

export default useHomeShowcaseData;
